import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { _ as _ts_generator } from "@swc/helpers/_/_ts_generator";
/**
 * AuthStore - Authentication state management
 *
 * Handles: OAuth tokens, token refresh, platform detection, scopes, impersonation
 *
 * Token Refresh Flow (ensureTokenFresh):
 * ┌─────────────────────────────────────────────────────────────────────────────┐
 * │ _refreshExhausted? ──Yes──► STOP                                           │
 * │         │No                                                                │
 * │ needsRefresh? ──No──► RETURN (token valid)                                 │
 * │         │Yes                                                               │
 * │ _refreshPromise? ──Yes──► Wait, recursive re-check                         │
 * │         │No                                                                │
 * │ GET /api/common/getNewTokens ◄──────────────────────────────┐              │
 * │         │                                                   │              │
 * │    Success ──► Update tokens, reset attempts, DONE          │              │
 * │    Error ────► attempts++ < 3? ──Yes──► Wait 2s/8s/30s ─────┘ (auto-retry) │
 * │                       │No                                                  │
 * │                       └──► Sentry.capture, _refreshExhausted = true        │
 * └─────────────────────────────────────────────────────────────────────────────┘
 */ import create from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { getStableIdFromToken } from '../services/secureStorageService';
import { getScopesFromToken } from '../helpers/authHelper';
import { isExternalUser } from '../helpers/capabilityHelpers';
import axios from 'axios';
import * as Sentry from '@sentry/browser';
import * as moment from 'moment';
var initialState = {
    platform: 'web',
    accessToken: '',
    graphAccessToken: '',
    graphRefreshToken: '',
    graphTokenExpires: null,
    powerBIAccessToken: '',
    powerBIRefreshToken: '',
    powerBITokenExpires: null,
    exchangeToken: '',
    idToken: '',
    oidFromIdToken: null,
    inOffice: false,
    teamsLogin: false,
    redirectedFromAdaptive: false,
    impersonateMode: false,
    impersonateOldUser: null,
    hasImpersonationRights: false,
    hasAdminConsent: false,
    scopes: [],
    myPeopleErrorAccessDenied: false,
    meetingErrorAccessDenied: false,
    showAllowAccessBanner: false,
    oldShowAllowAccessBanner: false,
    _refreshPromise: undefined,
    _refreshAttempts: 0,
    _refreshExhausted: false
};
var MAX_REFRESH_ATTEMPTS = 3;
var RETRY_DELAYS = [
    2000,
    8000,
    30000
] // 2s, 8s, 30s - industry standard backoff
;
// Add jitter (±20%) to prevent thundering herd
var getDelayWithJitter = function getDelayWithJitter(baseDelay) {
    var jitter = baseDelay * 0.2 * (Math.random() * 2 - 1 // ±20%
    );
    return Math.round(baseDelay + jitter);
};
var normalizeAuthState = function normalizeAuthState(payload) {
    return _object_spread_props(_object_spread({}, initialState, payload), {
        scopes: payload.scopes ? _to_consumable_array(payload.scopes) : []
    });
};
export var useAuthStore = create(//@ts-ignore
devtools(subscribeWithSelector(function(set, get) {
    return _object_spread_props(_object_spread({}, initialState), {
        setAuthState: function setAuthState(payload) {
            set(function() {
                return normalizeAuthState(payload);
            }, false, 'auth/setAuthState');
        },
        updateAuthState: function updateAuthState(payload) {
            var next = _object_spread({}, payload);
            if (payload.scopes !== undefined) {
                next.scopes = payload.scopes ? _to_consumable_array(payload.scopes) : [];
            }
            set(function(state) {
                return _object_spread({}, state, next);
            }, false, 'auth/updateAuthState');
        },
        resetAuthState: function resetAuthState() {
            set(function() {
                return _object_spread({}, initialState);
            }, false, 'auth/resetAuthState');
        },
        setGraphToken: function setGraphToken(graphAccessToken, graphTokenExpires) {
            set(function(state) {
                return _object_spread_props(_object_spread({}, state), {
                    graphAccessToken: graphAccessToken,
                    graphTokenExpires: graphTokenExpires !== null && graphTokenExpires !== void 0 ? graphTokenExpires : state.graphTokenExpires
                });
            }, false, 'auth/setGraphToken');
        },
        setIdToken: function setIdToken(idToken) {
            if (!idToken) return;
            set(function(state) {
                if (idToken === state.idToken) return state;
                return _object_spread_props(_object_spread({}, state), {
                    idToken: idToken,
                    oidFromIdToken: getStableIdFromToken(idToken)
                });
            }, false, 'auth/setIdToken');
        },
        setRedirectedFromAdaptive: function setRedirectedFromAdaptive(value) {
            set(function(state) {
                return _object_spread_props(_object_spread({}, state), {
                    redirectedFromAdaptive: value
                });
            }, false, 'auth/setRedirectedFromAdaptive');
        },
        unSetRedirectedFromAdaptive: function unSetRedirectedFromAdaptive() {
            if (get().platform === 'teams') {
                set(function(state) {
                    return _object_spread_props(_object_spread({}, state), {
                        redirectedFromAdaptive: false
                    });
                }, false, 'auth/unSetRedirectedFromAdaptive');
            }
        },
        needsRefresh: function needsRefresh() {
            var _get = get(), graphAccessToken = _get.graphAccessToken, graphTokenExpires = _get.graphTokenExpires, _refreshExhausted = _get._refreshExhausted;
            if (_refreshExhausted) return false;
            // External (CIAM) viewers never have a Graph token — keyed on userType
            if (isExternalUser()) return false;
            if (!graphAccessToken || !graphTokenExpires) return true;
            return moment(graphTokenExpires).subtract(1, 'minutes').isBefore(moment());
        },
        ensureTokenFresh: function ensureTokenFresh() {
            return _async_to_generator(function() {
                var existingPromise, refreshPromise;
                return _ts_generator(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            if (get()._refreshExhausted || !get().needsRefresh()) return [
                                2
                            ];
                            existingPromise = get()._refreshPromise;
                            if (!existingPromise) return [
                                3,
                                2
                            ];
                            return [
                                4,
                                existingPromise
                            ];
                        case 1:
                            _state.sent();
                            return [
                                2,
                                get().ensureTokenFresh()
                            ];
                        case 2:
                            refreshPromise = function() {
                                return _async_to_generator(function() {
                                    var data, error, attempts, baseDelay, delay;
                                    return _ts_generator(this, function(_state) {
                                        switch(_state.label){
                                            case 0:
                                                _state.trys.push([
                                                    0,
                                                    2,
                                                    ,
                                                    5
                                                ]);
                                                return [
                                                    4,
                                                    axios.get('/api/common/getNewTokens')
                                                ];
                                            case 1:
                                                data = _state.sent().data;
                                                if (!data.graphTokenExpires) {
                                                    throw new Error('getNewTokens failed to return graphTokenExpires');
                                                }
                                                set(function(state) {
                                                    return _object_spread_props(_object_spread({}, state), {
                                                        graphAccessToken: data.graphAccessToken,
                                                        graphTokenExpires: data.graphTokenExpires,
                                                        _refreshPromise: undefined,
                                                        _refreshAttempts: 0,
                                                        _refreshExhausted: false
                                                    });
                                                }, false, 'auth/ensureTokenFresh/success');
                                                return [
                                                    3,
                                                    5
                                                ];
                                            case 2:
                                                error = _state.sent();
                                                attempts = get()._refreshAttempts + 1;
                                                if (!(attempts < MAX_REFRESH_ATTEMPTS)) return [
                                                    3,
                                                    4
                                                ];
                                                baseDelay = RETRY_DELAYS[attempts - 1] || 2000;
                                                delay = getDelayWithJitter(baseDelay);
                                                return [
                                                    4,
                                                    new Promise(function(resolve) {
                                                        return setTimeout(resolve, delay);
                                                    })
                                                ];
                                            case 3:
                                                _state.sent();
                                                set(function(state) {
                                                    return _object_spread_props(_object_spread({}, state), {
                                                        _refreshPromise: undefined,
                                                        _refreshAttempts: attempts
                                                    });
                                                }, false, 'auth/ensureTokenFresh/retry');
                                                // Recursive retry after backoff delay
                                                return [
                                                    2,
                                                    get().ensureTokenFresh()
                                                ];
                                            case 4:
                                                Sentry.captureException(error, {
                                                    tags: {
                                                        context: 'ensureTokenFresh'
                                                    }
                                                });
                                                set(function(state) {
                                                    return _object_spread_props(_object_spread({}, state), {
                                                        _refreshPromise: undefined,
                                                        _refreshAttempts: 0,
                                                        _refreshExhausted: true
                                                    });
                                                }, false, 'auth/ensureTokenFresh/exhausted');
                                                return [
                                                    3,
                                                    5
                                                ];
                                            case 5:
                                                return [
                                                    2
                                                ];
                                        }
                                    });
                                })();
                            }();
                            set(function(state) {
                                return _object_spread_props(_object_spread({}, state), {
                                    _refreshPromise: refreshPromise
                                });
                            }, false, 'auth/ensureTokenFresh/start');
                            return [
                                4,
                                refreshPromise
                            ];
                        case 3:
                            _state.sent();
                            return [
                                2
                            ];
                    }
                });
            })();
        },
        // The single "give me a usable Graph token" primitive: refresh if near expiry, then return the
        // current token. Refresh-on-use consumers (e.g. presence) call this instead of duplicating the
        // ensureTokenFresh()-then-read pair.
        getValidGraphToken: function getValidGraphToken() {
            return _async_to_generator(function() {
                return _ts_generator(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            return [
                                4,
                                get().ensureTokenFresh()
                            ];
                        case 1:
                            _state.sent();
                            return [
                                2,
                                get().graphAccessToken
                            ];
                    }
                });
            })();
        }
    });
}), {
    name: 'AuthStore',
    enabled: process.env.NODE_ENV === 'development'
}));
export var PEOPLE_SCOPES = [
    'User.ReadBasic.All'
];
export var MEETING_SCOPES = [
    'Calendars.Read.Shared'
];
var toPrimitiveString = function toPrimitiveString(value) {
    if (value === null || value === undefined) return '';
    return value.toString();
};
var toPrimitiveBoolean = function toPrimitiveBoolean(value) {
    return !!value;
};
export var initAuthStateFromUser = function initAuthStateFromUser(user, platform) {
    var accessToken = toPrimitiveString(user.accessToken);
    var graphAccessToken = toPrimitiveString(user.graphAccessToken);
    var graphRefreshToken = toPrimitiveString(user.graphRefreshToken);
    var powerBIAccessToken = toPrimitiveString(user.powerBIAccessToken);
    var powerBIRefreshToken = toPrimitiveString(user.powerBIRefreshToken);
    var teamsLogin = toPrimitiveBoolean(user.teamsLogin);
    var impersonateMode = toPrimitiveBoolean(user.impersonateMode);
    var impersonateOldUser = user.impersonateOldUser;
    var hasImpersonationRights = toPrimitiveBoolean(user.hasImpersonationRights);
    var hasAdminConsent = toPrimitiveBoolean(user.hasAdminConsent);
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + accessToken;
    if (impersonateMode) {
        axios.defaults.headers.common['x-impersonate-mode'] = 'true';
    } else if (axios.defaults.headers.common['x-impersonate-mode']) {
        delete axios.defaults.headers.common['x-impersonate-mode'];
    }
    var existingAuthState = useAuthStore.getState();
    var scopes = [];
    if (graphAccessToken) {
        scopes = getScopesFromToken(graphAccessToken, 'initAuthStateFromUser');
    }
    if (powerBIAccessToken) {
        var powerBITokenScopes = getScopesFromToken(powerBIAccessToken, 'initAuthStateFromUser');
        scopes = _to_consumable_array(scopes).concat(_to_consumable_array(powerBITokenScopes));
    }
    var myPeopleErrorAccessDenied = existingAuthState.myPeopleErrorAccessDenied;
    var meetingErrorAccessDenied = existingAuthState.meetingErrorAccessDenied;
    var showAllowAccessBanner = existingAuthState.showAllowAccessBanner;
    var oldShowAllowAccessBanner = existingAuthState.oldShowAllowAccessBanner;
    if (scopes.length > 0) {
        var scopeSet = new Set(scopes);
        myPeopleErrorAccessDenied = PEOPLE_SCOPES.some(function(scope) {
            return !scopeSet.has(scope);
        });
        meetingErrorAccessDenied = MEETING_SCOPES.some(function(scope) {
            return !scopeSet.has(scope);
        });
        if (!window.location.href.includes('teamstabconfigure')) {
            showAllowAccessBanner = myPeopleErrorAccessDenied;
            oldShowAllowAccessBanner = showAllowAccessBanner;
        }
    }
    useAuthStore.getState().setAuthState(_object_spread_props(_object_spread({}, existingAuthState), {
        platform: platform,
        accessToken: accessToken,
        graphAccessToken: graphAccessToken,
        graphRefreshToken: graphRefreshToken,
        graphTokenExpires: user.graphTokenExpires,
        _refreshAttempts: user.graphTokenExpires ? 0 : existingAuthState._refreshAttempts,
        _refreshExhausted: user.graphTokenExpires ? false : existingAuthState._refreshExhausted,
        powerBIAccessToken: powerBIAccessToken,
        powerBIRefreshToken: powerBIRefreshToken,
        powerBITokenExpires: user.powerBITokenExpires,
        teamsLogin: teamsLogin,
        impersonateMode: impersonateMode,
        impersonateOldUser: impersonateOldUser,
        hasImpersonationRights: hasImpersonationRights,
        hasAdminConsent: hasAdminConsent,
        scopes: scopes,
        myPeopleErrorAccessDenied: myPeopleErrorAccessDenied,
        meetingErrorAccessDenied: meetingErrorAccessDenied,
        showAllowAccessBanner: showAllowAccessBanner,
        oldShowAllowAccessBanner: oldShowAllowAccessBanner
    }));
};
